import { useEffect, useRef } from 'react';

import { integrationsApi } from '@/api/queries/integrations';
import {
  isDesyncJob,
  isForwardJob,
  isJobActive,
  jobProviderLabel,
  syncJobsApi,
  type SyncJob,
} from '@/api/queries/syncJobs';
import { useToast, type ToastSeverity } from '@/components/ui';

import { SyncJobProgress } from './SyncJobProgress';

interface FinishedJobToast {
  severity: ToastSeverity;
  title: string;
  description?: string;
}

function finishedJobToast(job: SyncJob, provider: string): FinishedJobToast | null {
  const isDesync = isDesyncJob(job.type);
  const isForward = isForwardJob(job.type);

  if (job.status === 'failed') {
    return {
      severity: 'error',
      title: isForward
        ? `Auto-sync to ${provider} failed`
        : isDesync
          ? `Removing from ${provider} failed`
          : `Sync to ${provider} failed`,
      description: job.error || 'The sync job failed. Check the API logs.',
    };
  }

  if (job.status !== 'succeeded' || isForward) return null;

  const parts = (
    isDesync
      ? [
          job.deleted > 0 && `${job.deleted.toLocaleString()} deleted`,
          job.removed > 0 && `${job.removed.toLocaleString()} removed`,
        ]
      : [
          `${job.processed.toLocaleString()} processed`,
          job.added > 0 && `${job.added.toLocaleString()} added`,
          job.updated > 0 && `${job.updated.toLocaleString()} updated`,
          job.removed > 0 && `${job.removed.toLocaleString()} removed`,
          job.failed > 0 && `${job.failed.toLocaleString()} failed`,
        ]
  ).filter(Boolean) as string[];

  return {
    severity: job.failed > 0 ? 'info' : 'success',
    title: isDesync ? `Segment removed from ${provider}` : `Segment synced to ${provider}`,
    description: parts.join(' · ') || (isDesync ? 'Nothing to remove.' : undefined),
  };
}

export function SegmentSyncBadge({
  segmentId,
  pollWhenIdle,
}: {
  segmentId: string;
  pollWhenIdle?: boolean;
}) {
  const toast = useToast();
  const { data: jobs } = syncJobsApi.useActiveSyncJobs(segmentId, { pollWhenIdle });
  const { data: integrations } = integrationsApi.useList();
  const toastedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const job of jobs ?? []) {
      if (isJobActive(job.status) || toastedRef.current.has(job.id)) continue;
      toastedRef.current.add(job.id);
      const provider =
        integrations?.find((i) => i.id === job.integrationId)?.name ?? jobProviderLabel(job.type);
      const message = finishedJobToast(job, provider);
      if (message) toast.show(message);
    }
  }, [jobs, integrations, toast]);

  return <SyncJobProgress jobs={jobs ?? []} />;
}
